"use client";

import { supabase } from "@/lib/supabase";
import type { DbTool, DbToolTeam } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

type ToolTeamRow = {
  teams: { id: string; name: string } | { id: string; name: string }[] | null;
};

type ToolRow = Omit<DbTool, "teams"> & {
  tool_team_access?: ToolTeamRow[] | null;
};

function normalise(rows: ToolRow[] | null): DbTool[] {
  if (!rows) return [];

  return rows.map(({ tool_team_access, ...tool }) => {
    const teams = (tool_team_access ?? [])
      .map(({ teams: t }) =>
        Array.isArray(t) ? (t[0] ?? null) : t
      )
      .filter((t): t is DbToolTeam => Boolean(t))
      .reduce<DbToolTeam[]>((unique, t) => {
        if (unique.some((w) => w.id === t.id)) return unique;
        unique.push(t);
        return unique;
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));

    return { ...tool, teams };
  });
}

export type UseToolsReturn = {
  tools: DbTool[];
  loading: boolean;
  refresh: () => void;
};

export function useTools(): UseToolsReturn {
  const [tools, setTools] = useState<DbTool[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTools = useCallback(async () => {
    setLoading(true);

    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      setTools([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("tools")
      .select(`
        id, created_by, source_type, executor_type, scope,
        name, description, icon, enabled, schema, executor_config,
        created_at, updated_at,
        tool_team_access (
          teams (id, name)
        )
      `)
      .order("created_at", { ascending: false })
      .eq("enabled", true);

    if (error) {
      console.error("[useTools] fetch error:", error.message, error.details, error.hint, error.code);
    }

    setTools(normalise((data as ToolRow[] | null) ?? null));
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) void fetchTools();
    });

    return () => { cancelled = true; };
  }, [fetchTools]);

  return { tools, loading, refresh: fetchTools };
}
